import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { PaginatedResponse } from '@/types/api';
import { emptyStringValues } from '@/utils/objects';

import { useProgramFilters } from '../store/program-filters';
import { TProgram, TProgramPatch } from '../types';

export async function patchProgram(body: TProgramPatch): Promise<TProgram> {
  return mediaManagerClient
    .patch(`/api/v1/programs/${body.id}`, emptyStringValues(body))
    .then(response => response as unknown as TProgram);
}

type MutationFn = typeof patchProgram;

type TUsePatchProgramParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchProgram({ config }: TUsePatchProgramParams = {}) {
  const { gte, lte, state, channel_id, priorities, channel_media_type, paginationValues } =
    useProgramFilters();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    onError(error) {
      addErrorNotification(t('No se ha podido actualizar el programa'));
      console.error(error);
    },
    onSuccess(updatedProgram: TProgram) {
      addSuccessNotification(t('El programa se ha actualizado correctamente'));
      queryClient.setQueryData(
        [
          'programs',
          {
            gte,
            channel_id,
            lte,
            state,
            priorities,
            channel_media_type,
            ...Object.values(paginationValues || {}),
          },
        ],
        (oldPrograms: PaginatedResponse<TProgram> | undefined) => {
          if (!oldPrograms) {
            return {
              data: [updatedProgram],
              total: 1,
            };
          }
          return {
            ...oldPrograms,
            data: oldPrograms.data.map(program =>
              program.id === updatedProgram.id ? updatedProgram : program
            ),
          };
        }
      );
    },
    ...config,
    mutationFn: patchProgram,
  });
}
